import { API_URLS } from "../Constants/api.constants";
import axios from "axios";
import { storageKeys } from "../Constants/storage.key";
import { storage } from "./storage.service";
import { errorHandling } from "./error.handling";

let isRedirecting = false;
let refreshPromise = null;

// Quanto tempo antes de expirar devemos renovar proativamente (default: 60s).
const REFRESH_SKEW_MS =
  Number(process.env.REACT_APP_TOKEN_REFRESH_SKEW_MS) || 60000;

const isViewMapWindow = () =>
  typeof window !== "undefined" && window.location.hash.startsWith("#/viewmap");

const isLocalhost = () =>
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

// Encerramento DEFINITIVO da sessão (refresh token expirou de vez — fim dos 7 dias).
// Na janela do Radar (TV/popup): joga a janela principal pro login e fecha o popup.
// Nas demais janelas: redireciona pro login normalmente.
const handleSessionEnd = () => {
  localStorage.removeItem(storageKeys.access_and_token);

  if (isRedirecting) return;
  isRedirecting = true;

  if (isViewMapWindow()) {
    console.log("❌ Refresh token expirou (fim dos 7 dias). Encerrando sessão da TV...");
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.location = "/#/"; // manda a janela principal pro login
      }
    } catch (_e) {
      // opener inacessível (cross-origin/fechado) — ignora
    }
    window.close(); // fecha o popup da TV
    // fallback caso o navegador bloqueie window.close()
    window.location = "/#/";
    return;
  }

  console.log("❌ Refresh token expirou. Redirecionando para login...");
  window.location = "/#/";
};

const api = axios.create({
  headers: {
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Credentials": true,
  },
  crossDomain: true,
});

// Renovação de token com singleton — múltiplas requisições simultâneas (ou o
// refresh proativo + o reativo) compartilham a MESMA chamada de refresh.
// Marca o erro com `isRefreshTokenExpired` quando a falha é definitiva (401),
// para distinguir de falhas transitórias (rede, 500) que NÃO devem deslogar.
const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const tokenData = storage.get(storageKeys.access_and_token);

      if (!tokenData?.refreshToken) {
        const err = new Error("No refresh token available");
        err.isRefreshTokenExpired = true;
        throw err;
      }

      try {
        const refreshResponse = await axios.post(
          `${API_URLS.AUTH}/v3/auth/refresh-token`,
          { token: tokenData.token, refreshToken: tokenData.refreshToken }
        );

        const treated = errorHandling.handleResponse(
          refreshResponse,
          "api.refreshToken"
        );

        const newToken = treated?.token || treated?.Token;

        if (newToken) {
          // Mescla com os dados atuais e preserva o refreshToken antigo caso o
          // backend não devolva um novo (evita perder a cadeia dos 7 dias).
          const newTokenData = {
            ...tokenData,
            token: newToken,
            tokenExpiresAt:
              treated.tokenExpiresAt ||
              treated.TokenExpiresAt ||
              tokenData.tokenExpiresAt,
            refreshToken:
              treated.refreshToken ||
              treated.RefreshToken ||
              tokenData.refreshToken,
            refreshTokenExpiresAt:
              treated.refreshTokenExpiresAt ||
              treated.RefreshTokenExpiresAt ||
              tokenData.refreshTokenExpiresAt,
          };
          storage.save(storageKeys.access_and_token, newTokenData);
          return newTokenData;
        }

        // Resposta sem token novo — se veio 401, é expiração definitiva.
        const err = new Error("Refresh response invalid");
        if (treated?.status === 401) err.isRefreshTokenExpired = true;
        throw err;
      } catch (e) {
        // 401 no próprio refresh = refresh token expirado (fim dos 7 dias).
        if (e?.response?.status === 401) e.isRefreshTokenExpired = true;
        throw e;
      }
    })();

    // Limpa o singleton ao concluir (sucesso ou falha), sem engolir o erro.
    refreshPromise.catch(() => {}).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

api.interceptors.request.use(async (config) => {
  const requestUrl = (config?.url || "").toLowerCase();
  const isAuthEndpoint =
    requestUrl.includes("/auth/login") ||
    requestUrl.includes("/auth/password") ||
    requestUrl.includes("/authorized/login") ||
    requestUrl.includes("/refresh-token") ||
    requestUrl.includes("/email/configuration/validated");

  if (!isAuthEndpoint) {
    // Refresh PROATIVO: se o token está perto de expirar, renova ANTES de enviar.
    // Assim não mandamos uma chamada que já sabemos que vai voltar 401 — elimina
    // o 401 garantido a cada hora e a janela de risco que derrubava a TV.
    try {
      const tokenData = storage.get(storageKeys.access_and_token);
      if (tokenData?.tokenExpiresAt && tokenData?.refreshToken) {
        const expiresAt = new Date(tokenData.tokenExpiresAt).getTime();
        if (
          !Number.isNaN(expiresAt) &&
          expiresAt - Date.now() <= REFRESH_SKEW_MS
        ) {
          await refreshAccessToken();
        }
      }
    } catch (e) {
      // Falha definitiva no refresh proativo → encerra a sessão.
      // Falha transitória → segue; o 401 reativo (abaixo) trata na sequência.
      if (e?.isRefreshTokenExpired) {
        handleSessionEnd();
        return Promise.reject(e);
      }
    }

    let token = recoverToken();

    if (process.env.ENVIRONMENT === "TEST") {
      token = "bearer " + process.env.REACT_APP_MOCK_TOKEN;
    }

    if (token) {
      config.headers.Authorization = token;
    }
  }

  // Em localhost não existe domínio cadastrado no backend — força um domínio
  // de teste válido apenas neste caso. Fora de localhost, o domínio real do
  // cliente deve seguir intacto (nunca sobrescrever em produção).
  if (requestUrl.includes("domain") && isLocalhost()) {
    config.headers["domainName"] = "ofv2.dev.thetos.com.br";
  }

  if (!(config.data instanceof FormData)) {
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = (originalRequest?.url || "").toLowerCase();
    const isPublicValidateEmail = requestUrl.includes("/email/configuration/validated");

    if (isPublicValidateEmail) {
      return Promise.reject(error);
    }

    // 401 reativo — rede de segurança caso o refresh proativo não tenha rodado
    // (relógio dessincronizado, token revogado, sessão antiga sem tokenExpiresAt).
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newTokenData = await refreshAccessToken();

        if (newTokenData?.token) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `bearer ${newTokenData.token}`;
          return api(originalRequest); // re-tenta com o token NOVO
        }

        throw new Error("No token after refresh");
      } catch (refreshError) {
        // Falha DEFINITIVA (refresh token expirado / fim dos 7 dias) → encerra.
        if (refreshError?.isRefreshTokenExpired) {
          handleSessionEnd();
          return Promise.reject(refreshError);
        }
        // Falha TRANSITÓRIA (rede, 500 no refresh) → não desloga; o polling
        // tenta de novo no próximo ciclo.
        return Promise.reject(refreshError);
      }
    }

    // Re-tentativa também voltou 401 → o token novo não vale → sessão encerrada.
    if (error?.response?.status === 401) {
      handleSessionEnd();
    }

    return Promise.reject(error);
  }
);

const recoverToken = () => {
  const token = localStorage.getItem(storageKeys.access_and_token);
  if (token) {
    const parsed = JSON.parse(token);
    return "bearer " + parsed.token;
  }
  return null;
};

export default api;
